import React, { useState, useLayoutEffect, useRef, useId } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'

import { positionPopover, watchPopover } from './popover'

// Inline info-icon button that toggles a contextual popover with
// short explanatory copy. v7 deliberately avoids hover-tooltips
// (donate_v6/Tooltip.jsx wraps Bootstrap's tooltip + drops to a
// native title attribute on touch); a click-toggled popover plays
// nicer on touch and is reachable via keyboard out of the box.
//
// Implementation uses React-controlled visibility rather than the
// native HTML Popover API. The native API needs Safari 17+ while
// the rest of v7 targets Safari 16.4+ (container queries +
// color-mix), and the native API's top-layer rendering would also
// require CSS anchor positioning (Chrome 125+ only) to land near
// the trigger — we'd end up shimming as much as we save.
//
// The popover renders as a DOM sibling of the trigger button (not
// portaled to document.body) so VoiceOver can navigate to it via
// linear DOM traversal from the button. A previous portaled
// implementation was technically reachable via aria-owns, but VO
// support for aria-owns is too inconsistent to rely on.
//
// `boundary` constrains the popover's bounding box to a caller-
// supplied region — a ref, a CSS selector, or a DOM node. Defaults
// to the document element (full viewport) when omitted. Necessary
// because v7's `.widget-content` has overflow: hidden to contain
// panel-transition animations, so without explicit clamping the
// popover would get clipped at widget edges. The popover's max
// width also shrinks to fit narrow boundaries.
//
// Positioning: try below the trigger first; flip above if the
// boundary doesn't have room below but does above. Center
// horizontally on the trigger, clamp into the boundary's left/right
// edges so we never overflow.
//
// Accessibility: button uses the disclosure pattern (aria-expanded
// + aria-controls). Popover panel uses aria-live="polite" so
// screen readers announce the contents on open without forcing a
// focus shift. Dismisses on outside click and Escape; Escape
// returns focus to the trigger. The positioning math + the
// dismiss/reposition listeners live in ./popover so a plain-HTML
// consumer (e.g. a server-rendered menu) can share them.
const InfoPopover = ({ children, label, boundary }) => {
  const [ open, setOpen ] = useState(false)
  const [ position, setPosition ] = useState(null)
  const wrapperRef = useRef()
  const buttonRef = useRef()
  const popoverRef = useRef()
  const popoverId = useId()

  useLayoutEffect(() => {
    if (!open) return
    const button = buttonRef.current
    const popover = popoverRef.current
    const wrapper = wrapperRef.current
    if (!button || !popover || !wrapper) return

    // Position relative to the wrapper span (the popover is
    // position:absolute inside it) so it scrolls with the page;
    // reposition re-anchors + re-flips on scroll/resize.
    const reposition = () =>
      setPosition(positionPopover(button, popover, { boundary, relativeTo: wrapper }))

    reposition()
    return watchPopover(button, popover, {
      reposition,
      dismiss: reason => {
        setOpen(false)
        if (reason === 'escape') button.focus()
      },
    })
  }, [ open, boundary ])

  return (
    <span ref={wrapperRef} className="relative inline-block align-baseline">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={label}
        aria-expanded={open}
        aria-controls={popoverId}
        className="cursor-pointer text-gray-500 hover:text-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 rounded align-baseline"
      >
        <FontAwesomeIcon icon={faInfoCircle} />
      </button>
      {open && (
        <div
          ref={popoverRef}
          id={popoverId}
          // aria-live polite announces the popover content on open
          // without forcing focus into the panel; sibling DOM means
          // VoiceOver can also navigate to it linearly from the
          // trigger if the donor wants to re-read.
          aria-live="polite"
          // Hidden until useLayoutEffect computes the real position;
          // without this the popover would flash at the wrapper's
          // origin for one frame on open. visibility: hidden alone is
          // enough — keeping the element at the wrapper's natural
          // top/left avoids the RTL left-scroll trap that absolute
          // elements at left: -9999px fall into.
          style={position
            ? { top: position.top, left: position.left }
            : { visibility: 'hidden' }}
          className="absolute z-50 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700 shadow-lg"
        >
          {children}
        </div>
      )}
    </span>
  )
}

export default InfoPopover
